import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { api, rupee } from "@/lib/api";
import { useStore } from "@/context/StoreContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Package, MapPin, User as UserIcon, LogOut, Plus, Trash2, FileText, Download, ClipboardCheck, CheckCircle2, Clock, PackageCheck, Truck, Home } from "lucide-react";
import { pdfUrl } from "@/pages/Invoice";
import { toast } from "sonner";

const STATUS_COLORS = { New: "bg-blue-100 text-blue-700", Confirmed: "bg-indigo-100 text-indigo-700", Processing: "bg-amber-100 text-amber-700", Packed: "bg-purple-100 text-purple-700", Shipped: "bg-cyan-100 text-cyan-700", Delivered: "bg-green-100 text-green-700", Cancelled: "bg-red-100 text-red-700", Returned: "bg-orange-100 text-orange-700", Refunded: "bg-gray-100 text-gray-700" };

const STEPS = ["New", "Confirmed", "Processing", "Packed", "Shipped", "Delivered"];
const STEP_LABEL = { New: "Placed", Confirmed: "Confirmed", Processing: "Processing", Packed: "Packed", Shipped: "Shipped", Delivered: "Delivered" };
const STEP_ICON = { New: ClipboardCheck, Confirmed: CheckCircle2, Processing: Clock, Packed: PackageCheck, Shipped: Truck, Delivered: Home };

const OrderTimeline = ({ order }) => {
  const history = order.status_history || [{ status: order.status, at: order.created_at }];
  const reached = (s) => history.find((h) => h.status === s);
  const ended = ["Cancelled", "Returned", "Refunded"].includes(order.status);
  const idx = ended ? -1 : Math.max(0, STEPS.indexOf(order.status));
  return (
    <div className="mt-4 border-t border-border pt-4" data-testid={`timeline-${order.order_no}`}>
      {ended ? (
        <p className="text-sm font-medium text-red-700" data-testid="timeline-ended">Order {order.status.toLowerCase()} · {new Date(history[history.length - 1].at).toLocaleDateString()}</p>
      ) : (
        <ol className="flex items-start justify-between">
          {STEPS.map((s, i) => {
            const done = i <= idx; const Icon = STEP_ICON[s]; const h = reached(s);
            return (
              <li key={s} className="relative flex flex-1 flex-col items-center text-center">
                {i > 0 && <span className={`absolute right-1/2 top-4 h-0.5 w-full ${i <= idx ? "bg-burgundy" : "bg-border"}`} />}
                <span className={`relative z-10 grid h-8 w-8 place-items-center rounded-full border-2 ${done ? "border-burgundy bg-burgundy text-white" : "border-border bg-white text-muted-text"} ${i === idx ? "ring-4 ring-blush" : ""}`}><Icon className="h-4 w-4" /></span>
                <span className={`mt-2 text-[11px] font-medium ${done ? "text-burgundy" : "text-muted-text"}`}>{STEP_LABEL[s]}</span>
                {h && <span className="text-[10px] text-muted-text">{new Date(h.at).toLocaleDateString("en-IN", { day: "2-digit", month: "short" })}</span>}
              </li>
            );
          })}
        </ol>
      )}
      {order.tracking_id && <p className="mt-4 inline-block bg-cream px-3 py-2 text-sm" data-testid={`tracking-${order.order_no}`}><span className="text-xs uppercase tracking-widest text-burgundy">Tracking ID</span> <span className="ml-2 font-semibold">{order.tracking_id}</span></p>}
    </div>
  );
};

const Account = () => {
  const { user, logout } = useStore();
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [addresses, setAddresses] = useState([]);
  const [profile, setProfile] = useState({ name: "", phone: "" });
  const [newAddr, setNewAddr] = useState({ full_name: "", phone: "", line1: "", line2: "", city: "", state: "Gujarat", pincode: "" });

  useEffect(() => {
    if (user === null) { navigate("/login"); return; }
    if (user) {
      setProfile({ name: user.name, phone: user.phone || "" });
      api.get("/orders").then((r) => setOrders(r.data)).catch(() => {});
      api.get("/addresses").then((r) => setAddresses(r.data)).catch(() => {});
    }
  }, [user]);

  if (!user) return <div className="py-32 text-center font-serif text-2xl text-burgundy">Loading...</div>;

  const saveProfile = async () => { try { await api.put("/profile", profile); toast.success("Profile updated"); } catch { toast.error("Update failed"); } };
  const addAddress = async () => {
    if (!newAddr.full_name || !newAddr.line1 || !newAddr.pincode) { toast.error("Fill required fields"); return; }
    const r = await api.post("/addresses", newAddr); setAddresses([...addresses, r.data]);
    setNewAddr({ full_name: "", phone: "", line1: "", line2: "", city: "", state: "Gujarat", pincode: "" }); toast.success("Address added");
  };
  const delAddress = async (id) => { await api.delete(`/addresses/${id}`); setAddresses(addresses.filter((a) => a.id !== id)); };

  return (
    <div className="mx-auto max-w-5xl px-4 py-10 md:px-6">
      <div className="flex items-center justify-between">
        <div><h1 className="font-serif text-4xl text-burgundy">My Account</h1><p className="mt-1 text-muted-text">Welcome, {user.name}</p></div>
        {user.role === "admin" && <Button asChild variant="outline" className="rounded-none border-burgundy text-burgundy"><Link to="/admin">Admin Panel</Link></Button>}
      </div>

      <Tabs defaultValue="orders" className="mt-8">
        <TabsList className="rounded-none bg-transparent p-0">
          <TabsTrigger value="orders" className="rounded-none" data-testid="tab-orders"><Package className="mr-1.5 h-4 w-4" />Orders</TabsTrigger>
          <TabsTrigger value="addresses" className="rounded-none" data-testid="tab-addresses"><MapPin className="mr-1.5 h-4 w-4" />Addresses</TabsTrigger>
          <TabsTrigger value="profile" className="rounded-none" data-testid="tab-profile"><UserIcon className="mr-1.5 h-4 w-4" />Profile</TabsTrigger>
        </TabsList>

        <TabsContent value="orders" className="mt-6">
          {orders.length === 0 ? <p className="py-12 text-center text-muted-text" data-testid="no-orders">You haven't placed any orders yet.</p> : (
            <div className="space-y-4">
              {orders.map((o) => (
                <div key={o.id} className="border border-border p-4" data-testid={`order-${o.order_no}`}>
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div><p className="font-medium">{o.order_no}</p><p className="text-xs text-muted-text">{new Date(o.created_at).toLocaleDateString()} · {o.items.length} items</p></div>
                    <span className={`px-2.5 py-1 text-xs font-medium ${STATUS_COLORS[o.status] || "bg-gray-100"}`}>{o.status}</span>
                    <span className="font-semibold text-burgundy">{rupee(o.total)}</span>
                  </div>
                  <div className="mt-3 flex gap-2 overflow-x-auto">
                    {o.items.map((it, i) => <span key={i} className="whitespace-nowrap rounded-sm bg-cream px-2 py-1 text-xs">{it.name} ×{it.quantity}</span>)}
                  </div>
                  <OrderTimeline order={o} />
                  <div className="mt-3 flex gap-2">
                    <Button asChild size="sm" variant="outline" className="rounded-none border-burgundy text-burgundy"><Link to={`/invoice/${o.order_no}`} data-testid={`view-invoice-${o.order_no}`}><FileText className="mr-1.5 h-3.5 w-3.5" />View Invoice</Link></Button>
                    <Button asChild size="sm" variant="ghost" className="rounded-none"><a href={pdfUrl(o.order_no)} download data-testid={`download-invoice-${o.order_no}`}><Download className="mr-1.5 h-3.5 w-3.5" />Download PDF</a></Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="addresses" className="mt-6 space-y-4">
          {addresses.map((a) => (
            <div key={a.id} className="flex items-start justify-between border border-border p-4" data-testid={`address-${a.id}`}>
              <div className="text-sm"><p className="font-medium">{a.full_name} · {a.phone}</p><p className="text-muted-text">{a.line1}, {a.line2} {a.city}, {a.state} - {a.pincode}</p></div>
              <button onClick={() => delAddress(a.id)}><Trash2 className="h-4 w-4 text-muted-text hover:text-burgundy" /></button>
            </div>
          ))}
          <div className="border border-dashed border-border p-4">
            <h3 className="mb-3 flex items-center gap-2 font-medium"><Plus className="h-4 w-4" />Add New Address</h3>
            <div className="grid gap-3 sm:grid-cols-2">
              <Input placeholder="Full Name" value={newAddr.full_name} onChange={(e) => setNewAddr({ ...newAddr, full_name: e.target.value })} className="rounded-none" data-testid="addr-name" />
              <Input placeholder="Phone" value={newAddr.phone} onChange={(e) => setNewAddr({ ...newAddr, phone: e.target.value })} className="rounded-none" data-testid="addr-phone" />
              <Input placeholder="Address Line 1" value={newAddr.line1} onChange={(e) => setNewAddr({ ...newAddr, line1: e.target.value })} className="rounded-none sm:col-span-2" data-testid="addr-line1" />
              <Input placeholder="City" value={newAddr.city} onChange={(e) => setNewAddr({ ...newAddr, city: e.target.value })} className="rounded-none" data-testid="addr-city" />
              <Input placeholder="Pincode" value={newAddr.pincode} onChange={(e) => setNewAddr({ ...newAddr, pincode: e.target.value })} className="rounded-none" data-testid="addr-pincode" />
            </div>
            <Button onClick={addAddress} className="mt-3 rounded-none bg-burgundy" data-testid="save-address">Save Address</Button>
          </div>
        </TabsContent>

        <TabsContent value="profile" className="mt-6 max-w-md space-y-4">
          <div><Label>Name</Label><Input value={profile.name} onChange={(e) => setProfile({ ...profile, name: e.target.value })} className="mt-1 rounded-none" data-testid="profile-name" /></div>
          <div><Label>Email</Label><Input value={user.email} disabled className="mt-1 rounded-none bg-muted" /></div>
          <div><Label>Phone</Label><Input value={profile.phone} onChange={(e) => setProfile({ ...profile, phone: e.target.value })} className="mt-1 rounded-none" data-testid="profile-phone" /></div>
          <Button onClick={saveProfile} className="rounded-none bg-burgundy" data-testid="save-profile">Save Changes</Button>
        </TabsContent>
      </Tabs>

      <Button variant="outline" onClick={() => { logout(); navigate("/"); }} className="mt-8 rounded-none border-burgundy text-burgundy" data-testid="logout-btn"><LogOut className="mr-2 h-4 w-4" />Logout</Button>
    </div>
  );
};

export default Account;
